import { initializeApp } from "firebase/app";
import { getAuth, signOut as firebaseSignOut, onAuthStateChanged, User as FirebaseUser, connectAuthEmulator, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { initializeFirestore, persistentLocalCache, getFirestore, collection, doc, setDoc, onSnapshot, updateDoc, addDoc, deleteDoc, arrayUnion, arrayRemove, writeBatch, getDocs, query, where, getDoc, getDocFromServer, connectFirestoreEmulator, serverTimestamp, orderBy, limit } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator, httpsCallable } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { firebaseConfig } from "../firebaseConfig";
import { User, Team, Activity, Division, ActivityType, SystemSettings } from "../types";
import { MOCK_USERS, MOCK_TEAMS } from "../constants";

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = initializeFirestore(app, {
    localCache: persistentLocalCache()
});
export const storage = getStorage(app); // Initialize Storage
export const functions = getFunctions(app, "asia-southeast1");

// --- STORAGE HELPER ---
export const resizeImage = (file: File, maxWidth: number = 1920): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height *= maxWidth / width;
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);

                // Convert to WebP for better compression/quality ratio
                canvas.toBlob((blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error('Canvas to Blob failed'));
                }, 'image/webp', 0.85);
            };
            img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
    });
};

export const uploadImage = async (file: File, path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    // Resize before upload
    let blobToUpload: Blob = file;

    // Only resize images
    if (file.type.startsWith('image/')) {
        try {
            blobToUpload = await resizeImage(file);
        } catch (error) {
            console.warn("Resize failed, uploading original.", error);
        }
    }

    await uploadBytes(storageRef, blobToUpload);
    return await getDownloadURL(storageRef);
};

export const deleteImage = async (path: string): Promise<void> => {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
};

export const deleteActivityPhoto = async (userId: string, activityId: string, photo: any): Promise<void> => {
    // 1. Delete from Storage
    try {
        await deleteImage(photo.path);
    } catch (error) {
        console.warn("Failed to delete image from storage (might not exist):", error);
        // Continue to remove from Firestore even if storage fails
    }

    // 2. Remove from Firestore array
    const activityRef = doc(db, 'users', userId, 'activities', activityId);
    await updateDoc(activityRef, {
        photos: arrayRemove(photo)
    });
};

// Helper to convert Firestore data to our app types
const convertUser = (doc: any): User => ({ id: doc.id, ...doc.data() });
const convertTeam = (doc: any): Team => ({ id: doc.id, ...doc.data() });

// --- AUTH ---
export const signIn = async () => {
    // Dynamic import removed to fix "Pending promise was never set" error
    // const { signInWithPopup, GoogleAuthProvider } = await import("firebase/auth");
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
        const result = await signInWithPopup(auth, provider);
        const fbUser = result.user;

        // Enforce @techcoop.vn domain
        // if (!fbUser.email?.endsWith('@techcoop.vn')) {
        //     await firebaseSignOut(auth);
        //     throw new Error("Access restricted to @techcoop.vn emails only.");
        // }

        // ATOMIC USER CREATION: Use Cloud Function with transaction
        // This prevents race conditions when signing in from multiple devices
        // or after clearing cookies
        console.log('[SignIn] Calling ensureUserExists Cloud Function for:', fbUser.uid);
        const ensureUserExistsFn = httpsCallable(functions, 'ensureUserExists');
        const response: any = await ensureUserExistsFn({
            displayName: fbUser.displayName,
            email: fbUser.email,
            photoURL: fbUser.photoURL
        });

        console.log('[SignIn] ensureUserExists result:', response.data);

        // If user was just created and has a Google photo, self-host the avatar asynchronously
        // This doesn't block sign-in - the Google URL works immediately
        if (response.data?.created && fbUser.photoURL) {
            selfHostAvatar(fbUser.uid, fbUser.photoURL).catch(err => {
                console.warn('[SignIn] Avatar self-hosting failed (non-blocking):', err);
            });
        }

        // Check for suspension
        const userRef = doc(db, 'users', fbUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists() && userSnap.data().isSuspended) {
            await firebaseSignOut(auth);
            throw new Error("Tài khoản của bạn đã bị tạm khóa. Vui lòng liên hệ quản trị viên.");
        }

        return fbUser;
    } catch (error) {
        console.error("Login failed", error);
        throw error;
    }
};

// Helper: Self-host Google avatar to Firebase Storage (non-blocking)
const selfHostAvatar = async (userId: string, photoURL: string) => {
    try {
        const response = await fetch(photoURL);
        const blob = await response.blob();
        const file = new File([blob], "avatar.jpg", { type: blob.type || "image/jpeg" });

        const storagePath = `avatars/${userId}_${Date.now()}.jpg`;
        const resizedBlob = await resizeImage(file, 500);

        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, resizedBlob);
        const selfHostedUrl = await getDownloadURL(storageRef);

        // Update the user document with the self-hosted avatar
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { avatar: selfHostedUrl });
        console.log('[SignIn] Avatar self-hosted successfully for:', userId);
    } catch (error) {
        console.warn('[SignIn] Failed to self-host avatar:', error);
        // Non-critical - the Google URL still works
    }
};


export const completeOnboarding = async (userId: string) => {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
        hasCompletedOnboarding: true
    });
};

export const signOut = () => firebaseSignOut(auth);

// --- SEED DATA (For Demo) ---
export const seedDatabase = async () => {
    const batch = writeBatch(db);

    const teamsSnap = await getDocs(collection(db, 'teams'));
    if (teamsSnap.empty) {
        MOCK_TEAMS.forEach(team => {
            const ref = doc(db, 'teams', team.id);
            // Initialize with empty members so users are truly free agents
            batch.set(ref, { ...team, members: [] });
        });
    }

    // Always seed users if requested, but check for duplicates or just overwrite
    // We will mark them as seeded
    MOCK_USERS.forEach(user => {
        const ref = doc(db, 'users', user.id);
        // Remove teamId so they are free agents
        const { teamId, ...userWithoutTeam } = user;
        batch.set(ref, {
            ...userWithoutTeam,
            name: `${user.name} (Seeded)`,
            isSeeded: true,
            teamId: null
        }, { merge: true });
    });

    await batch.commit();
    console.log("Database seeded!");
};

export const clearSeedData = async () => {
    const batch = writeBatch(db);

    // Delete seeded users
    const usersQuery = query(collection(db, 'users'), where('isSeeded', '==', true));
    const usersSnap = await getDocs(usersQuery);
    usersSnap.forEach(doc => {
        batch.delete(doc.ref);
    });

    // Also remove them from teams if they are in any (though deleting user doc is main part)
    // Ideally we should clean up team references too, but for now let's just delete the users.
    // Actually, let's clean up team members array
    const teamsSnap = await getDocs(collection(db, 'teams'));
    teamsSnap.forEach(teamDoc => {
        const teamData = teamDoc.data() as Team;
        const newMembers = teamData.members.filter(mid => !usersSnap.docs.find(u => u.id === mid));
        if (newMembers.length !== teamData.members.length) {
            batch.update(teamDoc.ref, { members: newMembers });
        }
    });

    await batch.commit();
    console.log("Seed data cleared!");
};

// --- ADMIN ACTIONS ---

export const createTeam = async (name: string, avatar: string) => {
    const teamRef = await addDoc(collection(db, 'teams'), {
        name,
        avatar: avatar || `https://ui-avatars.com/api/?name=${name}&background=random`,
        members: []
    });
    return teamRef.id;
};

export const updateTeam = async (teamId: string, data: Partial<Team>) => {
    const teamRef = doc(db, 'teams', teamId);
    await updateDoc(teamRef, data);
};

export const addMemberToTeam = async (teamId: string, userId: string) => {
    const batch = writeBatch(db);

    // Update User
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, { teamId: teamId });

    // Update Team
    const teamRef = doc(db, 'teams', teamId);
    batch.update(teamRef, { members: arrayUnion(userId) });

    await batch.commit();
};

export const removeMemberFromTeam = async (teamId: string, userId: string) => {
    const batch = writeBatch(db);

    // Update User
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, { teamId: null }); // or delete field

    // Update Team
    const teamRef = doc(db, 'teams', teamId);
    batch.update(teamRef, { members: arrayRemove(userId) });

    await batch.commit();
};

export const deleteTeam = async (teamId: string) => {
    const batch = writeBatch(db);

    // Get current team to find members
    const teamRef = doc(db, 'teams', teamId);
    const teamSnap = await getDoc(teamRef);
    if (!teamSnap.exists()) return;

    const teamData = teamSnap.data() as Team;

    // Remove teamId from all members
    if (teamData.members && teamData.members.length > 0) {
        teamData.members.forEach((memberId) => {
            const userRef = doc(db, 'users', memberId);
            batch.update(userRef, { teamId: null });
        });
    }

    // Delete Team
    batch.delete(teamRef);

    await batch.commit();
};

export const toggleAdminStatus = async (userId: string, status: boolean) => {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { isAdmin: status });
};

export const addActivityToUser = async (userId: string, activityData: Omit<Activity, 'id' | 'points'> & { id?: string }) => {
    const roundedDistance = Math.round(activityData.distance * 100) / 100;

    // Values are calculated by Cloud Function trigger
    const points = 0;

    const activityId = activityData.id || `manual_${Date.now()}`;
    const newActivity = {
        id: activityId,
        ...activityData,
        distance: roundedDistance,
        points,
        manual: true,
        // Add defaults for missing fields to satisfy Activity type if strict,
        // but Firestore is lenient.
        isDeleted: false,
        date: activityData.date // Ensure date is passed
    };

    // Write to subcollection
    const activityRef = doc(db, 'users', userId, 'activities', activityId);
    await setDoc(activityRef, newActivity);

    // Note: totalPoints will be updated by the Cloud Function trigger
    return activityId;
};

export const deleteActivityFromUser = async (userId: string, activityId: string) => {
    // Hard delete provided activity in subcollection
    const activityRef = doc(db, 'users', userId, 'activities', activityId);
    await deleteDoc(activityRef);
};

export const deleteUser = async (userId: string, teamId: string | null) => {
    const batch = writeBatch(db);

    // 1. Delete all activities in subcollection
    const activitiesRef = collection(db, 'users', userId, 'activities');
    const activitiesSnap = await getDocs(activitiesRef);
    activitiesSnap.forEach(doc => {
        batch.delete(doc.ref);
    });

    // 2. Delete all notifications in subcollection
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const notificationsSnap = await getDocs(notificationsRef);
    notificationsSnap.forEach(doc => {
        batch.delete(doc.ref);
    });

    // 3. Remove from Team if applicable
    if (teamId) {
        const teamRef = doc(db, 'teams', teamId);
        batch.update(teamRef, { members: arrayRemove(userId) });
    }

    // 4. Delete User Document
    const userRef = doc(db, 'users', userId);
    batch.delete(userRef);

    await batch.commit();
};

export const suspendUser = async (userId: string, teamId: string | null) => {
    const batch = writeBatch(db);

    // 1. Remove from Team if applicable (consistency with hard delete logic)
    if (teamId) {
        const teamRef = doc(db, 'teams', teamId);
        batch.update(teamRef, { members: arrayRemove(userId) });
    }

    // 2. Set suspended flag and clear teamId
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
        isSuspended: true,
        teamId: null
    });

    await batch.commit();
};

export const restoreUser = async (userId: string) => {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
        isSuspended: false
    });
};

// --- SYSTEM SETTINGS ---
export const getSystemSettings = async (): Promise<SystemSettings> => {
    const docRef = doc(db, 'system', 'settings');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return docSnap.data() as SystemSettings;
    }
    return {};
};

export const updateSystemSettings = async (settings: SystemSettings) => {
    const docRef = doc(db, 'system', 'settings');
    await setDoc(docRef, settings, { merge: true });
};

export const recalculateAllPointsTrigger = httpsCallable(functions, 'recalculateAllPoints');
export const clearSocialDataTrigger = httpsCallable(functions, 'clearSocialData');

export const createSyncJob = async (forceFullSync: boolean) => {
    return addDoc(collection(db, 'system_jobs'), {
        type: 'SYNC_ALL_USERS',
        forceFullSync,
        status: 'pending',
        createdAt: serverTimestamp(),
        progress: 0,
        total: 0
    });
};

export const subscribeToLatestJob = (callback: (job: any) => void) => {
    const q = query(
        collection(db, 'system_jobs'),
        where('type', '==', 'SYNC_ALL_USERS'),
        orderBy('createdAt', 'desc'),
        limit(1)
    );

    return onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
            callback({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
        } else {
            callback(null);
        }
    });
};

// --- USER & TEAM HELPERS ---

export const getTeams = async (): Promise<Team[]> => {
    const teamsSnap = await getDocs(collection(db, 'teams'));
    return teamsSnap.docs.map(convertTeam);
};

export const updateUserProfile = async (userId: string, data: { name: string; division: Division; teamId: string }) => {
    const userRef = doc(db, 'users', userId);
    const updates: any = {
        name: data.name,
        division: data.division,
        teamId: data.teamId || null
    };

    if (data.teamId) {
        const batch = writeBatch(db);
        batch.update(userRef, updates);

        const teamRef = doc(db, 'teams', data.teamId);
        batch.update(teamRef, { members: arrayUnion(userId) });

        await batch.commit();
    } else {
        await updateDoc(userRef, updates);
    }
};

export const setUserDivision = async (userId: string, division: Division) => {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { division });
};